import React, { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import BlockIcon from '@mui/icons-material/Block'
import SyncIcon from '@mui/icons-material/Sync'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip, { MEMBERSHIP_STATUS_CHIP } from '../../component/StatusChip'
import { FORM_COMPACT_WARNING_ACTION_BUTTON_SX } from '../../component/BottomButtonGroup'
import { formatLocalDateTime } from '../../utils/dateDisplay'
import { formatNumber } from '../../utils/numberDisplay'
import { formatMembershipPaidAmount, formatMembershipPlanPrice, getMembershipDisplayStatus, getMembershipEntitlementLabel, getMembershipTrialLabel } from './membershipDisplay'

const Field = ({ label, value }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, gap: 1, py: 0.75, borderBottom: '1px solid rgba(148, 163, 184, 0.18)' }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2">{value || '-'}</Typography>
    </Box>
)

const Section = ({ title, children }) => (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'rgba(148, 163, 184, 0.24)' }}>
        <Typography variant="h6" sx={{ mb: 1.5, fontSize: 16 }}>{title}</Typography>
        {children}
    </Paper>
)

export default function Membership(props) {
    const history = useHistory()
    const location = useLocation()
    const membershipId = props.match.params.membershipId
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState(null)
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('error')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [actionLoading, setActionLoading] = useState(false)

    const getData = useCallback(async ({ showLoading = true } = {}) => {
        try {
            if (showLoading) setLoading(true)
            const response = await API.post('/api/v1/admin/membership/getDetail', { membershipId })
            const body = response.data

            if (body.code === 200) {
                setData(body.data)
            } else if (body.code === 403 || body.code === 401) {
                history.push('/login')
            } else {
                setMessageSnackbar(body.message || '멤버십 상세를 불러오지 못했습니다.')
                setSeveritySnackbar('error')
                setOpenSnackbar(true)
            }
        } catch (_error) {
            setMessageSnackbar('멤버십 상세를 불러오지 못했습니다.')
            setSeveritySnackbar('error')
            setOpenSnackbar(true)
        } finally {
            if (showLoading) setLoading(false)
        }
    }, [history, membershipId])

    useEffect(() => {
        getData()
    }, [getData])

    const goBack = () => {
        history.push({
            pathname: '/memberships',
            search: location.state?.search || '',
        })
    }

    const handleVoidCoupons = async () => {
        if (!window.confirm('미사용 멤버십 쿠폰을 회수하시겠습니까? 이미 사용된 쿠폰은 회수되지 않습니다.')) return

        try {
            setActionLoading(true)
            const response = await API.post('/api/v1/admin/membership/voidCoupons', {
                membershipId,
                reason: 'admin_void',
            })
            const body = response.data

            if (body.code === 200) {
                setMessageSnackbar('미사용 멤버십 쿠폰을 회수했습니다.')
                setSeveritySnackbar('success')
                setOpenSnackbar(true)
                await getData({ showLoading: false })
            } else if (body.code === 403 || body.code === 401) {
                history.push('/login')
            } else {
                setMessageSnackbar(body.message || '쿠폰 회수에 실패했습니다.')
                setSeveritySnackbar('error')
                setOpenSnackbar(true)
            }
        } catch (_error) {
            setMessageSnackbar('쿠폰 회수에 실패했습니다.')
            setSeveritySnackbar('error')
            setOpenSnackbar(true)
        } finally {
            setActionLoading(false)
        }
    }

    const handleResyncStore = async () => {
        if (!window.confirm('스토어 구독 상태를 다시 검증하시겠습니까? 검증 결과에 따라 멤버십 상태와 쿠폰이 갱신될 수 있습니다.')) return

        try {
            setActionLoading(true)
            const response = await API.post('/api/v1/admin/membership/resyncStore', { membershipId })
            const body = response.data

            if (body.code === 200) {
                setMessageSnackbar('스토어 구독 상태를 다시 검증했습니다.')
                setSeveritySnackbar('success')
                setOpenSnackbar(true)
                await getData({ showLoading: false })
            } else if (body.code === 403 || body.code === 401) {
                history.push('/login')
            } else {
                setMessageSnackbar(body.message || '스토어 재검증에 실패했습니다.')
                setSeveritySnackbar('error')
                setOpenSnackbar(true)
            }
        } catch (_error) {
            setMessageSnackbar('스토어 재검증에 실패했습니다.')
            setSeveritySnackbar('error')
            setOpenSnackbar(true)
        } finally {
            setActionLoading(false)
        }
    }

    if (loading) return <Spiner />

    const membership = data?.membership || {}
    const user = membership.user || {}
    const plan = membership.membership_plan || {}
    const canResyncStore = ['google', 'apple'].includes(membership.store)
    const displayStatus = getMembershipDisplayStatus(membership)

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                    <Title>멤버십 상세</Title>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Button size="small" variant="outlined" startIcon={<SyncIcon />} onClick={handleResyncStore} disabled={actionLoading || !canResyncStore} title={canResyncStore ? '스토어 구독 상태 재검증' : '현재 Google/Apple 구독만 지원합니다.'}>
                            스토어 재검증
                        </Button>
                        <Button size="small" variant="outlined" color="warning" sx={FORM_COMPACT_WARNING_ACTION_BUTTON_SX} startIcon={<BlockIcon />} onClick={handleVoidCoupons} disabled={actionLoading}>
                            쿠폰 회수
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<ArrowBackIcon />} onClick={goBack} disabled={actionLoading}>
                            목록
                        </Button>
                    </Box>
                </Box>
                <Section title="회원 및 구독 상태">
                    <Field label="회원" value={`${user.nickName || user.username || '-'} (${user.userId || user.email || '-'})`} />
                    <Field label="상태" value={<StatusChip status={displayStatus} config={MEMBERSHIP_STATUS_CHIP} />} />
                    {displayStatus && displayStatus !== membership.status && (
                        <Field label="원장 상태" value={<StatusChip status={membership.status} config={MEMBERSHIP_STATUS_CHIP} />} />
                    )}
                    <Field label="혜택 권한" value={getMembershipEntitlementLabel(membership)} />
                    <Field label="스토어" value={membership.store} />
                    <Field label="상품" value={plan.planType === 'monthly' ? '월간' : plan.planType === 'yearly' ? '연간' : plan.planType} />
                    <Field label="상품 ID" value={plan.productId} />
                    <Field label="상품 기준가(원화)" value={formatMembershipPlanPrice(plan.priceKrw)} />
                    <Field label="무료체험" value={getMembershipTrialLabel(membership)} />
                    <Field label="자동갱신" value={membership.autoRenewStatus === 1 ? 'ON' : membership.autoRenewStatus === 0 ? 'OFF' : '-'} />
                    <Field label="시작일" value={formatLocalDateTime(membership.startedAt)} />
                    <Field label="만료 예정" value={formatLocalDateTime(membership.expiresAt)} />
                    <Field label="혜택 종료" value={formatLocalDateTime(membership.benefitUntil)} />
                    <Field label="다음 결제" value={formatLocalDateTime(membership.nextBillingAt)} />
                    <Field label="최근 결제기간 시작" value={formatLocalDateTime(membership.renewalPeriodStartedAt || membership.lastRenewedAt)} />
                    <Field label="원장 갱신" value={formatLocalDateTime(membership.ledgerUpdatedAt)} />
                    <Field label="해지 예약" value={formatLocalDateTime(membership.cancelScheduledAt)} />
                    <Field label="환불 감지" value={formatLocalDateTime(membership.refundedAt)} />
                </Section>
                <Section title="구매 이력">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>상태</TableCell>
                                <TableCell>유형</TableCell>
                                <TableCell>주문</TableCell>
                                <TableCell>결제금액</TableCell>
                                <TableCell>구매일</TableCell>
                                <TableCell>만료일</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data?.purchases || []).map((row) => (
                                <TableRow key={row.purchaseId}>
                                    <TableCell>{row.purchaseId}</TableCell>
                                    <TableCell>{row.status}</TableCell>
                                    <TableCell>{row.purchaseType}</TableCell>
                                    <TableCell>{row.storeOrderId || row.transactionId || '-'}</TableCell>
                                    <TableCell>{formatMembershipPaidAmount(row)}</TableCell>
                                    <TableCell>{formatLocalDateTime(row.purchasedAt)}</TableCell>
                                    <TableCell>{formatLocalDateTime(row.expiresAt)}</TableCell>
                                </TableRow>
                            ))}
                            {(!data?.purchases || data.purchases.length === 0) && (
                                <TableRow><TableCell colSpan={7}>구매 이력이 없습니다.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Section>
                <Section title="스토어 이벤트">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>스토어</TableCell>
                                <TableCell>이벤트</TableCell>
                                <TableCell>처리상태</TableCell>
                                <TableCell>수신일</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data?.events || []).map((row) => (
                                <TableRow key={row.eventId}>
                                    <TableCell>{row.eventId}</TableCell>
                                    <TableCell>{row.store}</TableCell>
                                    <TableCell>{row.eventType}{row.subType ? ` / ${row.subType}` : ''}</TableCell>
                                    <TableCell>{row.processStatus}</TableCell>
                                    <TableCell>{formatLocalDateTime(row.receivedAt)}</TableCell>
                                </TableRow>
                            ))}
                            {(!data?.events || data.events.length === 0) && (
                                <TableRow><TableCell colSpan={5}>이벤트 이력이 없습니다.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Section>
                <Section title="쿠폰 발급 이력">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>기간</TableCell>
                                <TableCell>수량</TableCell>
                                <TableCell>금액</TableCell>
                                <TableCell>상태</TableCell>
                                <TableCell>사유</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data?.couponIssues || []).map((row) => (
                                <TableRow key={row.couponIssueId}>
                                    <TableCell>{row.couponIssueId}</TableCell>
                                    <TableCell>{formatLocalDateTime(row.periodStartAt)} ~ {formatLocalDateTime(row.periodEndAt)}</TableCell>
                                    <TableCell>{row.issuedCouponCount}</TableCell>
                                    <TableCell>{formatNumber(row.couponAmount)}원</TableCell>
                                    <TableCell>{row.status}</TableCell>
                                    <TableCell>{row.reason}</TableCell>
                                </TableRow>
                            ))}
                            {(!data?.couponIssues || data.couponIssues.length === 0) && (
                                <TableRow><TableCell colSpan={6}>쿠폰 발급 이력이 없습니다.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Section>
            </Paper>
            <Snackbars open={openSnackbar} onClose={() => setOpenSnackbar(false)} severity={severitySnackbar} message={messageSnackbar} />
        </Grid>
    )
}
